<template>
    <v-dialog :value="value" persistent max-width="780px">
        <v-card v-if="record" class="overflow-auto" style="max-height: 100vh">
            <v-card-title>
                {{ $t('add_new_contact') }}
            </v-card-title>
            <v-card-text class="overflow-auto">
                <v-container fluid class="form-content pa-5">
                    <v-form ref="form" v-model="formValid">
                        <slot name="before-form"></slot>
                        <div class="d-flex align-center">
                            <h4 class="mb-2">Infos</h4>
                            <v-spacer />
                            <PublishedIndicator :record="record" />
                        </div>

                        <!-- Legal Type -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-radio-group
                                    dense
                                    row
                                    :value="record.legal_type"
                                    :rules="[rules.required]"
                                    @change="onInput('legal_type', $event)"
                                >
                                    <v-radio :label="$t('person_label')" value="person" />
                                    <v-radio :label="$t('organization_label')" value="organization" />
                                </v-radio-group>
                            </v-col>
                        </v-row>

                        <!-- Bezeichnung -->
                        <v-row dense>
                            <v-col cols="12">
                                <LabelField
                                    :label="
                                        record.legal_type === 'person' ? $t('bezeichnung_person') : $t('bezeichnung')
                                    "
                                >
                                    <v-text-field
                                        :value="record.title"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        placeholder="Wondergirl, Superman, ..."
                                        :counter="60"
                                        :rules="
                                            [
                                                record.legal_type === 'organization' ? rules.required : null,
                                                rules.maxLength(60),
                                            ].filter(Boolean)
                                        "
                                        @input="onInput('title', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- Geschlecht -->
                        <v-row v-if="record.legal_type === 'person'" dense>
                            <v-col cols="12">
                                <LabelField :label="$t('anrede')">
                                    <v-radio-group dense :value="record.sex" row @change="onInput('sex', $event)">
                                        <v-radio :label="$t('frau')" value="f" />
                                        <v-radio :label="$t('herr')" value="m" />
                                        <v-radio :label="$t('divers')" value="d" />
                                    </v-radio-group>
                                </LabelField>
                            </v-col>
                        </v-row>
                        <!-- Vor-/Nachname -->
                        <v-row v-if="record.legal_type === 'person'" dense>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('vorname')">
                                    <v-text-field
                                        :value="record.first_name"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.required, rules.maxLength(100)]"
                                        @input="onInput('first_name', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('nachname')">
                                    <v-text-field
                                        :value="record.surname"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.required, rules.maxLength(100)]"
                                        @input="onInput('surname', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- Adresse -->
                        <h4 class="mb-2 mt-5">{{ $t('adresse') }}</h4>
                        <v-row dense>
                            <v-col cols="12" lg="5">
                                <LabelField :label="$t('strasse')">
                                    <v-text-field
                                        :value="record.street"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(100)]"
                                        @input="onInput('street', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="1">
                                <LabelField :label="$t('strasse_nr')">
                                    <v-text-field
                                        :value="record.street_nr"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(15)]"
                                        @input="onInput('street_nr', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('zusatz')">
                                    <v-text-field
                                        :value="record.address_supplement"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(100)]"
                                        @input="onInput('address_supplement', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <!-- plz/ort -->
                        <v-row dense>
                            <v-col cols="12" lg="2">
                                <LabelField :label="$t('plz')">
                                    <v-text-field
                                        :value="record.zip"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.between(1000, 99999)]"
                                        @input="onInput('zip', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="4">
                                <LabelField :label="$t('ort')">
                                    <v-text-field
                                        :value="record.city"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(100)]"
                                        @input="onInput('city', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('postfach')">
                                    <v-text-field
                                        :value="record.po_box"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(100)]"
                                        @input="onInput('po_box', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- land -->
                        <v-row dense>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('land')">
                                    <CountrySelectField
                                        :value="record.country_id"
                                        hide-details="auto"
                                        item-value="id"
                                        :label="null"
                                        @input="onInput('country_id', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6"></v-col>
                        </v-row>

                        <h4 class="mt-5 mb-2">{{ $t('contact') }}</h4>
                        <!-- tel -->
                        <v-row dense>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('tel_1')">
                                    <v-text-field
                                        :value="record.tel"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(50)]"
                                        @input="onInput('tel', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('tel_2')">
                                    <v-text-field
                                        :value="record.mobile"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.maxLength(50)]"
                                        @input="onInput('mobile', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <!-- mail -->
                        <v-row dense>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('email')">
                                    <v-text-field
                                        :value="record.email"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.email, rules.maxLength(255)]"
                                        @input="onInput('email', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <LabelField :label="$t('webseite')">
                                    <UrlField
                                        :value="record.web"
                                        :rules="[rules.maxLength(255)]"
                                        @input="onInput('web', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <slot name="after-form"></slot>
                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <PlainBtn @click="onCloseBtn">{{ $t('schliessen') }}</PlainBtn>
                <ActionBtn :loading="loading" :disabled="!formValid" @click="onSaveBtn">
                    <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                    {{ $t('save_addr') }}
                </ActionBtn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { formRules } from '@/helpers/tools';
import PublishedIndicator from '@/components/address/PublishedIndicator.vue';
import CountrySelectField from '@/components/country/SelectField.vue';
import { Address } from '@/models/Address';

/**
 * This dialog shows an existing AddressContact record and allows editing
 * the Relation Function.
 * Emits:
 * - close: when "cancel" is pressed
 * - saved(AddressContact) when the contact record was saved
 */
export default {
    name: 'SimpleAddressDlg',
    components: { PublishedIndicator, CountrySelectField },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        loading: { type: Boolean, default: false },
        record: {
            type: Address,
            default: null,
        },
    },
    data() {
        return {
            formValid: true,
        };
    },
    created() {
        this.rules = formRules;
    },
    mounted() {
        this.validateForm();
    },
    methods: {
        validateForm() {
            this.$refs.form.validate();
        },
        onInput(field, value) {
            this.record.set(field, value);
        },
        onSaveBtn() {
            this.$emit('save-btn', this.record);
        },
        onCloseBtn() {
            this.$emit('input', false);
        },
    },
};
</script>
